import { jsx, jsxs } from 'react/jsx-runtime';
import { Component } from 'react';
import PropTypes from 'prop-types';
import isoLanguageConverter from 'iso-language-converter/index.es5';
import shaka from 'shaka-player';
import groupBy from 'lodash/groupBy';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { PlayerState, PlayerViewMode, Provider } from '@dtvgo/player-context';
import { NpawAdapterProvider } from '@dtvgo/npaw-context';
import ValueStore from '@dtvgo/value-store';
import NpawAdapterShaka, { NpawAdsAdapterShaka, defaultErrorParser } from '@dtvgo/npaw-adapter-shaka';
import { exitFullscreen, removeFullscreenChangeEventListener, AUDIO_LANGUAGE_NOT_AVAILABLE, PLAYER_TRACKS_EVENTS, PlayerError, getFullscreenElement, requestFullscreen, getDefaultLowLatencyConfig, addFullscreenChangeEventListener, KEY_SYSTEM_NAMES } from '@dtvgo/player-common-utils';
import { normalizeError, RequestTypeMapping } from '@dtvgo/shaka-player-utils';
import Backoff from './Backoff.js';
import MediaTailorAdManager from './MediaTailorAdManager.js';
import { hasSourceChanged, DEFAULT_ERROR_CODES_TO_IGNORE_LIVE, DEFAULT_ERROR_CODES_TO_IGNORE_VOD, DEFAULT_ERROR_CODES_TO_RELOAD, DEFAULT_ERROR_CODES_TO_RELOAD_LIVE, DELAYED_THRESHOLD_SECONDS, getManifestUrlFromSource, getTrackingURLFromManifest, keepSingleAudioTrack, makeSameLanguageAudioTracksDifferent, MAP_KEY_SYSTEM_TO_PROP, TRY_TO_EXTRACT_FROM_INIT_DATA, EXTRACT_FROM_INIT_DATA, applyRequestModifications, PROGRESS_UPDATE_INTERVAL, BUFFERING_TIMEOUT_MS, DEFAULT_SECONDS_TO_REWIND } from './utils.js';
import './ShakaPlayer.scss';
import 'lodash/defaults';
import './AdsStats.js';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const usedProps = [
  "config",
  "bufferConfig",
  "source",
  "shouldPlay",
  "readyToBuild",
  "recovery.reload.backoffOptions",
  "recovery.asWarnings.errorCodes",
  "className",
  "children"
];
const {
  Player,
  net: { NetworkingEngine: { RequestType } },
  polyfill,
  util: { Error: ShakaError, EventManager },
  text: { UITextDisplayer }
} = shaka;
polyfill.installAll();
class ShakaPlayer extends Component {
  constructor(props) {
    super(props);
    __publicField(this, "eventManager", new EventManager());
    this.state = {
      npawAdapter: void 0,
      npawAdsAdapter: void 0,
      state: new ValueStore(PlayerState.UNKNOWN),
      buffering: new ValueStore(false),
      seeking: new ValueStore({ isSeeking: false, time: void 0 }),
      viewMode: new ValueStore(PlayerViewMode.INLINE),
      time: new ValueStore(0),
      currentTime: new ValueStore(0),
      duration: new ValueStore(0),
      maxTimeShift: new ValueStore(0),
      volume: new ValueStore(100),
      audioTracks: new ValueStore([]),
      subtitleTracks: new ValueStore([]),
      isLicenseExpired: new ValueStore(false)
    };
    this.startBufferingTimeout = this.startBufferingTimeout.bind(this);
    this.onBuffering = this.onBuffering.bind(this);
    this.onSourceUnloaded = this.onSourceUnloaded.bind(this);
    this.onSubtitleChanged = this.onSubtitleChanged.bind(this);
    this.onSeekedOrTimeShifted = this.onSeekedOrTimeShifted.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.onProgress = this.onProgress.bind(this);
    this.onDurationChange = this.onDurationChange.bind(this);
    this.onVolumeChanged = this.onVolumeChanged.bind(this);
    this.onFullscreenChange = this.onFullscreenChange.bind(this);
    this.npawErrorParser = this.npawErrorParser.bind(this);
    this.npawErrorDetailsGetter = this.npawErrorDetailsGetter.bind(this);
    this.setAvailableSubtitles = this.setAvailableSubtitles.bind(this);
    this.setAvailableAudioLanguages = this.setAvailableAudioLanguages.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.togglePlayPause = this.togglePlayPause.bind(this);
    this.goToFullScreen = this.goToFullScreen.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);
    this.rewind = this.rewind.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.setProgress = this.setProgress.bind(this);
    this.goToLive = this.goToLive.bind(this);
    this.setVolume = this.setVolume.bind(this);
    this.toggleMute = this.toggleMute.bind(this);
    this.getAvailableAudio = this.getAvailableAudio.bind(this);
    this.getAvailableSubtitles = this.getAvailableSubtitles.bind(this);
    this.selectAudioTrack = this.selectAudioTrack.bind(this);
    this.selectSubtitleTrack = this.selectSubtitleTrack.bind(this);
    this.disableSubtitles = this.disableSubtitles.bind(this);
    this.getMaxTimeShift = this.getMaxTimeShift.bind(this);
    this.setCurrentTime = debounce(this.setCurrentTime.bind(this), 300);
    this.setVideoElement = (videoElement) => {
      this.videoElement = videoElement;
    };
    this.setAdContainerElement = (adContainerElement) => {
      this.adContainerElement = adContainerElement;
    };
    this.setContainerElement = (containerElement) => {
      this.containerElement = containerElement;
    };
    this.isDestroying = false;
    this.isErrorSources = false;
    this.reloadTime = void 0;
    this.playerActions = {
      play: this.play,
      pause: this.pause,
      togglePlayPause: this.togglePlayPause,
      goToFullScreen: this.goToFullScreen,
      exitFullScreen: exitFullscreen,
      toggleFullScreen: this.toggleFullScreen,
      seek: this.setProgress,
      rewind: this.rewind,
      goToLive: this.goToLive,
      setVolume: this.setVolume,
      toggleMute: this.toggleMute,
      selectAudioTrack: this.selectAudioTrack,
      selectSubtitleTrack: this.selectSubtitleTrack,
      disableSubtitles: this.disableSubtitles,
      getAvailableAudio: this.getAvailableAudio,
      getAvailableSubtitles: this.getAvailableSubtitles
    };
  }
  /**
   * Executed when the component is mounted
   * It sets localization config for Shaka, builds the player and builds the UI for the player
   */
  componentDidMount() {
    const { readyToBuild, source } = this.props;
    if (readyToBuild) {
      this.buildPlayer();
      if (source) {
        this.loadSource();
      }
    }
  }
  shouldComponentUpdate(nextProps, nextState) {
    return usedProps.some((prop) => get(this.props, prop) !== get(nextProps, prop)) || Object.entries(this.state).some(([key, value]) => value !== nextState[key]);
  }
  /**
   * Executed when the component was updated.
   * It checks for some changes in props and state and updates the player
   * according to that:
   * 1. Checks for changes in source to pause and unload current source
   * 2. Checks for changes in source and entitlements token to load source when both are
   * defined
   * 3. Checks for changes in the contentLoaded state and execute a test playback to fire
   * errors.
   * 4. Checks for changes in the shouldPlay prop and starts playback in the player
   * @param {object} prevProps - The props used in the render before the actual one
   * @param {object} prevState - The state used in the render before the actual one
   */
  componentDidUpdate(prevProps) {
    const {
      source: prevSource,
      shouldPlay: prevShouldPlay,
      config: prevConfig,
      bufferConfig: prevBufferConfig,
      recovery: {
        asWarning: { errorCodes: prevAsWarningErrorCodes } = {},
        reload: { backoffOptions: prevBackoffOptions } = {}
      }
    } = prevProps;
    const {
      source,
      shouldPlay,
      readyToBuild,
      avoidAutoPlay,
      config,
      bufferConfig,
      recovery: {
        asWarning: { errorCodes: asWarningErrorCodes } = {},
        reload: { backoffOptions } = {}
      }
    } = this.props;
    let loading = false;
    if (!this.alreadyBuilt && readyToBuild) {
      this.buildPlayer();
      this.loadSource();
      loading = true;
    }
    if (!this.alreadyBuilt)
      return;
    if (config?.streaming && !isEqual(config.streaming, prevConfig?.streaming)) {
      this.player.configure("streaming", config.streaming);
      console.log("Player changed config:", this.player.getConfiguration());
    }
    if (bufferConfig !== prevBufferConfig && bufferConfig) {
      this.setBufferConfig(bufferConfig);
    }
    if (hasSourceChanged(source, prevSource) && !loading) {
      this.manifestForReload = void 0;
      this.reloadBackoff.reset();
      this.clearUnrecoveredError();
      this.unloadSource();
      this.loadSource();
    }
    if (shouldPlay !== prevShouldPlay && shouldPlay && !avoidAutoPlay && this.readyToPlay) {
      this.startPlayback();
    }
    if (prevBackoffOptions !== backoffOptions) {
      this.reloadBackoff?.setOptions(backoffOptions);
    }
    if (prevAsWarningErrorCodes !== asWarningErrorCodes) {
      const { npawAdapter } = this.state;
      npawAdapter?.setOptions({
        nonFatalErrorCodes: [...this.getAsWarningsErrorCodes()]
      });
    }
  }
  /**
   * Executed before the component is unmounted
   * Removes all event listeners
   * Destroys the player and remove progress interval
   */
  componentWillUnmount() {
    this.eventManager.removeAll();
    removeFullscreenChangeEventListener(this.onFullscreenChange);
    this.cancelBufferingTimeout();
    this.clearProgressInterval();
    this.setCurrentTime.cancel();
    this.isDestroying = true;
    this.clearUnrecoveredError();
    if (this.reloadBackoff)
      this.reloadBackoff.destroy();
    const { npawAdapter, npawAdsAdapter } = this.state;
    if (npawAdapter)
      npawAdapter.dispose();
    if (npawAdsAdapter)
      npawAdsAdapter.dispose();
    if (this.player)
      this.player.destroy();
    this.player = void 0;
    this.adManager = void 0;
  }
  onReadyToPlay() {
    const { shouldPlay, onReadyToPlay, avoidAutoPlay } = this.props;
    this.readyToPlay = true;
    onReadyToPlay();
    if (shouldPlay && !avoidAutoPlay) {
      this.startPlayback();
    }
  }
  /**
   * Executed when a seek or time shift action is completed on the player
   * It removes the buffering state and notifies parent component
   */
  onSeekedOrTimeShifted() {
    const { onSeeked } = this.props;
    this.player.configure("abr.enabled", true);
    const { seeking } = this.state;
    seeking.set({ isSeeking: false, time: void 0 });
    this.onBufferingEnded();
    this.onProgress();
    onSeeked();
  }
  /**
   * Executed when the playback
   */
  onPlaying(event) {
    const { onPlaying } = this.props;
    const { state } = this.state;
    state.set(PlayerState.PLAYING);
    onPlaying(event);
    if (!this.isChangingAudio) {
      this.onBufferingEnded();
    }
    this.clearUnrecoveredError();
    this.reloadBackoff.scheduleReset();
  }
  /**
   * Executed when player should enter to buffering state
   * It cancels previous scheduled calls to this function and notifies parent component
   */
  onBuffering() {
    const { onBuffering } = this.props;
    this.cancelBufferingTimeout();
    const { buffering } = this.state;
    if (!buffering.get()) {
      buffering.set(true);
      onBuffering();
    }
  }
  /**
   * Executed when player should abandon buffering state.
   * It cancels previous scheduled onBuffering calls and notifies parent component
   */
  onBufferingEnded() {
    const { onBufferingEnded } = this.props;
    this.cancelBufferingTimeout();
    const { buffering } = this.state;
    if (buffering.get()) {
      buffering.set(false);
      onBufferingEnded();
    }
  }
  /**
   * Method to be executed when a source can't be loaded in
   * the player
   */
  onSourceUnloaded() {
    const { onSourceUnloaded } = this.props;
    onSourceUnloaded();
    this.onBufferingEnded();
    this.clearProgressInterval();
    this.setCurrentTime.cancel();
  }
  onSubtitleChanged() {
    this.setAvailableSubtitles();
    this.onBufferingEnded();
  }
  /**
   * Method to be executed when a duration change event is detected in
   * the player to notify parent component
   */
  onDurationChange() {
    const { onDurationChange } = this.props;
    const { duration } = this.player.getMediaElement();
    const { duration: durationStore } = this.state;
    durationStore.set(duration);
    onDurationChange(duration);
  }
  /**
   * Method to be executed when the volume of the player changes
   */
  onVolumeChanged() {
    const { onVolumeChanged } = this.props;
    const isMuted = this.videoElement.muted;
    const volume = isMuted ? 0 : this.videoElement.volume * 100;
    const { volume: volumeStore } = this.state;
    volumeStore.set(volume);
    onVolumeChanged({ isMuted, volume });
  }
  /**
   * @method onError
   * @param {shaka.extern.Error} error
   * @description Method handles the error events.
   */
  onError(error, isFromLoad = false) {
    const { recovery: { reload }, onWarning } = this.props;
    const mutableError = error;
    mutableError.isFromLoad = isFromLoad;
    const { severity, code, handled } = error;
    if (severity === ShakaError.Severity.RECOVERABLE || handled || !isFromLoad && this.getAsWarningsErrorCodes().has(code)) {
      mutableError.ignored = true;
      console.warn("Reporting error as warning => Ignored", error);
      onWarning(normalizeError(error));
      return;
    }
    if (code === ShakaError.Code.EXPIRED && this.ignoreShakaExpiredLicenseError) {
      mutableError.ignored = true;
      this.ignoreShakaExpiredLicenseError = false;
      console.warn("Reporting error as warning => Ignored expired license", error);
      onWarning(normalizeError(error));
      return;
    }
    if ((this.getReloadErrorCodes().has(code) || reload?.shouldReload?.(normalizeError(error))) && this.reloadBackoff.attempt()) {
      this.clearUnrecoveredError();
      mutableError.handled = true;
      this.recoveryError = error;
      console.warn("Reload will be made because an error", error);
      onWarning(normalizeError(error));
      return;
    }
    this.clearUnrecoveredError();
    this.reportErrorToParent(error);
  }
  /**
   * Executed while the user is watching the content and current time
   * gets updated. It calculates the progress and the seconds left and notifies the
   * parent component.
   */
  onProgress() {
    if (!this.player || this.isErrorSources)
      return;
    const {
      isLive,
      onProgress,
      isCatchUpActive = true,
      source: { isLowLatency }
    } = this.props;
    const progress = isLive ? this.getLiveProgress() : this.player?.getMediaElement()?.currentTime;
    const currentTime = isLive ? Date.now() / 1e3 : 0;
    this.playerTime = isLive ? currentTime + this.getTimeShift() : progress;
    const { time, currentTime: currentTimeStore } = this.state;
    time.set(progress);
    currentTimeStore.set(currentTime);
    onProgress(progress, currentTime);
    if (isLowLatency && isCatchUpActive) {
      this.lowLatencyCatchup();
    }
  }
  onFullscreenChange() {
    const { onFullScreenMode } = this.props;
    const isFullScreen = this.isFullScreen();
    const { viewMode } = this.state;
    viewMode.set(isFullScreen ? PlayerViewMode.FULL_SCREEN : PlayerViewMode.INLINE);
    onFullScreenMode(isFullScreen);
  }
  getAsWarningsErrorCodes() {
    const { isLive, recovery: { asWarning } } = this.props;
    return asWarning?.errorCodes || isLive && DEFAULT_ERROR_CODES_TO_IGNORE_LIVE || DEFAULT_ERROR_CODES_TO_IGNORE_VOD;
  }
  getReloadErrorCodes() {
    const { isLive, recovery: { reload } } = this.props;
    return reload?.errorCodes || /* @__PURE__ */ new Set([
      ...DEFAULT_ERROR_CODES_TO_RELOAD,
      ...isLive ? DEFAULT_ERROR_CODES_TO_RELOAD_LIVE : []
    ]);
  }
  /**
   * @method getMaxTimeShift
   * @description Returns the limit in seconds for time shift. Is either negative or 0.
   */
  getMaxTimeShift() {
    if (!this.player.isLive())
      return 0;
    const seekRange = this.player?.seekRange();
    if (!seekRange)
      return 0;
    const maxTimeShift = seekRange.start - seekRange.end + 5;
    return maxTimeShift > 0 ? 0 : maxTimeShift;
  }
  /**
   * @method getAvailableAudio
   * @description Method that returns the available audio from variant tracks.
   */
  getAvailableAudio() {
    const variantTracks = this.player?.getVariantTracks?.();
    if (!variantTracks)
      return [];
    const current = variantTracks.find(({ active }) => active);
    const audioQualities = Object.values(groupBy(variantTracks, "audioId")).map(([variantTrack]) => ({
      // Audio properties
      label: variantTrack.label,
      language: variantTrack.language,
      parsedLanguage: variantTrack.language.split("$")[0],
      roles: variantTrack.audioRoles,
      // Quality properties
      quality: {
        id: variantTrack.originalAudioId,
        bitrate: variantTrack.audioBandwidth,
        codec: variantTrack.audioCodec,
        active: variantTrack.audioId === current.audioId
      }
    }));
    return Object.values(groupBy(audioQualities, "language")).map((audios) => ({
      id: audios[0].language,
      label: audios[0].label,
      lang: audios[0].parsedLanguage,
      role: audios[0].roles?.map((value) => ({ value })),
      name: `${isoLanguageConverter(audios[0].parsedLanguage.toLowerCase()) || AUDIO_LANGUAGE_NOT_AVAILABLE}`,
      iso: isoLanguageConverter(audios[0].parsedLanguage.toLowerCase(), { to: 1 }),
      isSelected: audios.some(({ quality: { active } }) => active),
      getQualities: () => audios.map(({ quality: { active, ...quality } }) => quality)
    }));
  }
  /**
   * @method setUserPreferenceLanguage
   * @description Method that set the current audio language base it on the user preference account.
   */
  setUserPreferenceLanguage() {
    const {
      userLanguages: {
        audio: userPreferenceLanguage = "",
        subtitles: subtitlesPreference = ""
      }
    } = this.props;
    if (userPreferenceLanguage) {
      const availableAudio = this.getAvailableAudio();
      const userPreferenceAudioTrack = availableAudio.find(
        (audioTrack) => audioTrack.iso === userPreferenceLanguage
      );
      if (userPreferenceAudioTrack) {
        this.selectAudioTrack(userPreferenceAudioTrack);
      }
    }
    if (subtitlesPreference) {
      const subtitlesList = this.player.getTextTracks();
      const subtitles = subtitlesList.map((subtitleTrack2) => ({
        isSelected: subtitleTrack2.active,
        name: isoLanguageConverter(subtitleTrack2.language.toLowerCase()),
        iso: isoLanguageConverter(subtitleTrack2.language.toLowerCase(), { to: 1 }),
        ...subtitleTrack2
      }));
      const subtitleTrack = subtitles.find(
        (track) => isoLanguageConverter(track.language.toLowerCase(), { to: 1 }) === subtitlesPreference
      );
      if (subtitleTrack) {
        this.player.setTextTrackVisibility(true);
        this.selectSubtitleTrack(subtitleTrack);
        this.onSubtitleChanged();
      }
    }
  }
  /**
  * @method setAvailableAudioLanguages
  * @description Method that set the available audio languages.
  */
  setAvailableAudioLanguages() {
    const { onTracksEvent } = this.props;
    const availableAudio = this.getAvailableAudio();
    const { audioTracks } = this.state;
    audioTracks.set(availableAudio);
    onTracksEvent({
      event: PLAYER_TRACKS_EVENTS.SET_AVAILABLE_AUDIO_LANGUAGES,
      param: availableAudio
    });
  }
  getAvailableSubtitles() {
    const subtitlesList = this.player?.getTextTracks?.();
    if (!subtitlesList)
      return [];
    return subtitlesList.map((subtitleTrack) => ({
      isSelected: subtitleTrack.active,
      name: isoLanguageConverter(subtitleTrack.language.toLowerCase()),
      kind: subtitleTrack.kind === "caption" ? "captions" : subtitleTrack.kind,
      iso: isoLanguageConverter(subtitleTrack.language.toLowerCase(), { to: 1 }),
      ...subtitleTrack,
      id: subtitleTrack.originalTextId,
      originalShakaId: subtitleTrack.id
    }));
  }
  /**
   * Sets the availabe subtitles
   */
  setAvailableSubtitles() {
    const { onTracksEvent } = this.props;
    const availableSubtitles = this.getAvailableSubtitles();
    const { subtitleTracks } = this.state;
    subtitleTracks.set(availableSubtitles);
    onTracksEvent({
      event: PLAYER_TRACKS_EVENTS.SET_AVAILABLE_SUBTITLE_TRACKS,
      param: availableSubtitles
    });
  }
  getTimeShift() {
    const { end } = this.player.seekRange();
    const { currentTime } = this.player.getMediaElement();
    if (!end)
      return 0;
    return currentTime - end;
  }
  /**
   * Gets the current progress within the current live content
   */
  getLiveProgress() {
    const { seeking } = this.state;
    if (seeking.get().time !== void 0) {
      return seeking.get().time;
    }
    return this.getTimeShift();
  }
  setBufferConfig(bufferConfig) {
    const { backward, forward } = bufferConfig;
    this.player.configure("streaming.bufferBehind", backward);
    this.player.configure("streaming.bufferingGoal", forward);
  }
  /**
   * Seeks to a new time on the player
   * @param {number} progress - The progress value to seek to
   */
  setProgress(progress) {
    const { isLive, onSeeking } = this.props;
    let newTimeShift;
    let newProgress;
    let seekingValue = progress;
    const seekRange = this.player.seekRange();
    if (isLive) {
      const maxTimeShift = this.getMaxTimeShift();
      newProgress = seekRange.end + seekingValue;
      newTimeShift = progress;
      this.manualTimeShift = true;
      if (newTimeShift <= maxTimeShift) {
        seekingValue = maxTimeShift;
        newProgress = seekRange.end + maxTimeShift;
      } else if (newTimeShift > -DELAYED_THRESHOLD_SECONDS) {
        seekingValue = 0;
        newProgress = seekRange.end;
        this.manualTimeShift = false;
      }
    } else {
      const duration = seekRange.end;
      newProgress = progress;
      if (newProgress > duration) {
        seekingValue = duration - 1;
        newProgress = duration - 1;
      } else if (newProgress < 0) {
        seekingValue = 0;
        newProgress = 0;
      }
    }
    const { seeking } = this.state;
    seeking.set({ isSeeking: true, time: seekingValue });
    this.setCurrentTime(newProgress);
    onSeeking(seekingValue);
  }
  setCurrentTime(progress) {
    const mediaElement = this.player.getMediaElement();
    if (progress === mediaElement.currentTime) {
      this.onSeekedOrTimeShifted();
      return;
    }
    this.player.getMediaElement().currentTime = progress;
  }
  /**
   * Set a new volume on the player
   * @param {number} newVolume - The volume value to set into the player
   * For the volume value 0 the state will be muted
   */
  setVolume(newVolume) {
    this.videoElement.muted = newVolume === 0;
    this.videoElement.volume = newVolume / 100;
    this.onVolumeChanged();
  }
  clearUnrecoveredError() {
    if (this.unrecoveredErrorTimeout)
      clearTimeout(this.unrecoveredErrorTimeout);
    this.recoveryError = void 0;
    this.unrecoveredErrorTimeout = void 0;
  }
  reportErrorToParent(error) {
    const {
      onHDCPError,
      onNonPlayableSources,
      onUnrecoverableError,
      onFailedLicense
    } = this.props;
    const MAP_ERROR_TO_CALLBACK = {
      [PlayerError.Code.DRM_RESTRICTED_OUTPUT]: onHDCPError,
      [PlayerError.Code.LICENSE_REQUEST_FAILED]: onFailedLicense,
      [PlayerError.Category.NETWORK]: onNonPlayableSources,
      [PlayerError.Category.STREAMING]: onNonPlayableSources,
      [PlayerError.Category.MANIFEST]: onNonPlayableSources
    };
    const normalizedError = normalizeError(error);
    const callback = MAP_ERROR_TO_CALLBACK[normalizedError?.code] || MAP_ERROR_TO_CALLBACK[normalizedError?.category] || onUnrecoverableError;
    if (callback === onNonPlayableSources) {
      this.isErrorSources = true;
    }
    this.clearProgressInterval();
    if (callback) {
      setTimeout(() => {
        callback(normalizedError);
      }, 0);
    }
  }
  isFullScreen() {
    return getFullscreenElement() === this.containerElement;
  }
  goToFullScreen() {
    requestFullscreen(this.containerElement);
  }
  toggleFullScreen() {
    if (this.isFullScreen()) {
      exitFullscreen();
    } else {
      this.goToFullScreen();
    }
  }
  clearProgressInterval() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = void 0;
    }
  }
  changePlaybackRate(targetPlaybackRate) {
    if (this.player.getPlaybackRate() !== targetPlaybackRate) {
      this.player.trickPlay(targetPlaybackRate);
    }
  }
  lowLatencyCatchup() {
    const { lowLatency = {} } = this.props;
    const {
      targetLatency,
      catchup: {
        playbackRate: catchupPlaybackRate,
        seekThreshold
      } = {},
      fallback: { playbackRate: fallbackPlaybackRate } = {}
    } = getDefaultLowLatencyConfig(lowLatency);
    const delay = -this.getTimeShift();
    if (this.player.getMediaElement().paused) {
      if (delay > targetLatency + seekThreshold)
        this.manualTimeShift = true;
      return;
    }
    if (this.manualTimeShift && delay < targetLatency + seekThreshold) {
      this.manualTimeShift = false;
    }
    if (this.manualTimeShift)
      return;
    if (delay === targetLatency) {
      this.player.cancelTrickPlay();
    } else if (delay < targetLatency) {
      this.changePlaybackRate(fallbackPlaybackRate);
    } else if (delay > targetLatency) {
      if (delay > targetLatency + seekThreshold) {
        const { seeking } = this.state;
        if (seeking.get().time !== void 0) {
          this.selectLowestBandwidthVariant();
          this.goToLive();
        }
      } else {
        this.changePlaybackRate(catchupPlaybackRate);
      }
    }
  }
  selectLowestBandwidthVariant() {
    const allVariants = this.player.getVariantTracks();
    const currentVariant = allVariants.find(({ active }) => active);
    const selectableVariants = allVariants.filter(({ language }) => language === currentVariant.language);
    let minimumBandwidthIndex = 0;
    for (let index = 1; index < selectableVariants.length; index += 1) {
      if (selectableVariants[index].bandwidth < selectableVariants[minimumBandwidthIndex].bandwidth) {
        minimumBandwidthIndex = index;
      }
    }
    this.player.configure("abr.enabled", false);
    const lowestBandwidthVariant = selectableVariants[minimumBandwidthIndex];
    this.player.selectVariantTrack(lowestBandwidthVariant);
  }
  /**
   * Builds Shaka Player
   */
  buildPlayer() {
    this.licenseRequestCounter = 0;
    this.ignoreShakaExpiredLicenseError = false;
    const {
      bufferConfig,
      config: {
        streaming = {},
        manifest: {
          dash = {},
          ...manifest
        } = {},
        ...config
      } = {},
      recovery: { reload: { backoffOptions } = {} }
    } = this.props;
    const {
      backward,
      forward
    } = bufferConfig || {};
    Player.setAdManagerFactory(() => new MediaTailorAdManager());
    this.player = new Player(this.videoElement);
    this.adManager = /** @type {MediaTailorAdManager} */
    this.player.getAdManager();
    if (this.adManager) {
      this.adManager.initCustomMediaTailor(
        this.adContainerElement,
        this.player.getNetworkingEngine(),
        this.videoElement,
        this.player
      );
    }
    this.reloadBackoff = new Backoff({
      ...backoffOptions,
      operation: () => {
        const { npawAdapter } = this.state;
        const {
          recovery: { reload: { source, skipSeconds = 0, unrecoveredErrorSeconds } = {} } = {}
        } = this.props;
        if (source) {
          this.manifestForReload = getManifestUrlFromSource(source);
        }
        npawAdapter.setIgnoreUnloadingStop(true);
        this.reloadTime = this.playerTime + skipSeconds;
        this.unrecoveredErrorTimeout = setTimeout(() => {
          if (this.reloadBackoff.attempt()) {
            console.warn("Reload will be made because an unrecovered error", this.recoveryError);
          } else {
            this.reportErrorToParent(this.recoveryError);
            this.recoveryError = void 0;
          }
          this.unrecoveredErrorTimeout = void 0;
        }, (unrecoveredErrorSeconds || 20) * 1e3);
        this.loadSource();
      }
    });
    this.alreadyBuilt = true;
    const streamingConfig = bufferConfig && {
      bufferBehind: backward,
      bufferingGoal: forward
    };
    const mergedConfig = {
      ...config,
      streaming: {
        ...streamingConfig,
        ...streaming
      },
      manifest: {
        ...manifest,
        dash: {
          ...dash,
          manifestPreprocessor: (manifestXML) => {
            if (this.adManager && !this.adManager.hasTrackingUrl()) {
              this.adManager.setTrackingUrl(getTrackingURLFromManifest(manifestXML));
            }
            const { singleAudioTrackLanguage } = this.props;
            let manifestToUse = manifestXML;
            if (dash.manifestPreprocessor) {
              manifestToUse = dash.manifestPreprocessor(manifestToUse);
            }
            const periods = manifestToUse.querySelectorAll("Period");
            for (const period of periods) {
              if (singleAudioTrackLanguage)
                keepSingleAudioTrack(period, singleAudioTrackLanguage);
              makeSameLanguageAudioTracksDifferent(period);
            }
            return manifestToUse;
          }
        }
      },
      textDisplayFactory: () => new UITextDisplayer(this.videoElement, this.containerElement)
    };
    this.player.configure(mergedConfig);
    console.log("Player init with config:", this.player.getConfiguration());
    this.player.getNetworkingEngine().registerRequestFilter(async (type, request) => {
      const {
        source,
        keySystemsConfig,
        onRequest
      } = this.props;
      request.allowCrossSiteCredentials = this.shouldAllowCrossSiteCredentials(type) ?? request.allowCrossSiteCredentials;
      request.headers = {
        ...request.headers,
        ...this.obtainAdditionalHeaders(type)
      };
      if (type === RequestType.LICENSE) {
        const keySystem = this.player.keySystem();
        const keySystemConfig = keySystemsConfig?.[MAP_KEY_SYSTEM_TO_PROP[keySystem]];
        const url = keySystemConfig?.getLicenseUrl?.(
          this.player.drmInfo().initData[0],
          true
        );
        if (url)
          request.uris.unshift(url);
        request.uris = request.uris.map((uri) => uri === TRY_TO_EXTRACT_FROM_INIT_DATA && EXTRACT_FROM_INIT_DATA[keySystem] ? EXTRACT_FROM_INIT_DATA[keySystem](this.player.drmInfo().initData[0]) : uri);
        this.licenseRequestCounter += 1;
        this.ignoreShakaExpiredLicenseError = this.licenseRequestCounter >= 2;
        setTimeout(() => {
          this.licenseRequestCounter = 0;
        }, 6e3);
        request.headers = {
          ...request.headers,
          ...await keySystemConfig?.getLicenseHeaders?.({
            method: request.method,
            url: request.uris[0],
            data: request.body
          }, source)
        };
      }
      if (onRequest) {
        try {
          const modifiedRequest = await onRequest(RequestTypeMapping[type], request);
          applyRequestModifications(request, modifiedRequest);
        } catch (error) {
          console.error("[ShakaPlayer] Error executing onRequest", error);
        }
      }
    });
    this.player.getNetworkingEngine().registerResponseFilter(async (type, response) => {
      const { onResponseReceived } = this.props;
      if (onResponseReceived) {
        try {
          const modifiedResponse = await onResponseReceived(RequestTypeMapping[type], response);
          response.data = modifiedResponse?.data ?? response.data;
        } catch (error) {
          console.error("[ShakaPlayer] Error executing onResponseReceived", error);
        }
      }
      if (type === RequestType.MANIFEST) {
        this.manifestForReload = response.uri;
      }
    });
    this.addPlayerEventListeners();
    this.setState({
      npawAdapter: new NpawAdapterShaka(this.player, this.videoElement, {
        errorParser: this.npawErrorParser,
        getErrorDetails: this.npawErrorDetailsGetter,
        nonFatalErrorCodes: [...this.getAsWarningsErrorCodes()]
      }),
      npawAdsAdapter: new NpawAdsAdapterShaka(this.adManager, {
        errorParser: this.npawErrorParser,
        getErrorDetails: this.npawErrorDetailsGetter
      })
    });
    const { state } = this.state;
    state.set(PlayerState.UNLOADED);
  }
  shouldAllowCrossSiteCredentials(requestType) {
    const { crossSiteCredentials } = this.props;
    const mapRequestTypeToCrossSiteCredentialsField = {
      [RequestType.MANIFEST]: "manifest",
      [RequestType.SEGMENT]: "segment",
      [RequestType.LICENSE]: "license"
    };
    return crossSiteCredentials?.[mapRequestTypeToCrossSiteCredentialsField[requestType]];
  }
  obtainAdditionalHeaders(requestType) {
    const { getManifestHeaders, getSegmentHeaders } = this.props;
    const mapRequestTypeToHeadersGetter = {
      [RequestType.MANIFEST]: getManifestHeaders,
      [RequestType.SEGMENT]: getSegmentHeaders
    };
    return mapRequestTypeToHeadersGetter[requestType]?.();
  }
  /**
   * Executes a test play of the media element.
   *
   * This function is called when the media source is loaded.
   * Its purpose is to detect if the player is ready to play.
   */
  async testPlay() {
    const mediaElement = this.player.getMediaElement();
    const prevVolume = mediaElement.volume;
    mediaElement.volume = 0;
    try {
      await mediaElement.play();
      mediaElement.pause();
    } catch (error) {
      console.error("Error occurred during test play:", error);
    }
    mediaElement.volume = prevVolume;
    this.onReadyToPlay();
  }
  /**
   * Registers event listeners for Shaka player
   */
  addPlayerEventListeners() {
    this.eventManager.listen(this.player, "loaded", () => {
      this.setAvailableAudioLanguages();
      this.setAvailableSubtitles();
      this.disableSubtitles();
      this.setUserPreferenceLanguage();
      this.onDurationChange();
      this.onVolumeChanged();
      this.progressInterval = setInterval(this.onProgress, PROGRESS_UPDATE_INTERVAL);
      this.testPlay();
    });
    this.eventManager.listen(this.videoElement, "durationchange", this.onDurationChange);
    this.eventManager.listen(this.videoElement, "playing", this.onPlaying);
    this.eventManager.listen(this.videoElement, "play", (event) => {
      const { onPlay } = this.props;
      const { state } = this.state;
      state.set(
        [PlayerState.PAUSED, PlayerState.LOADED].includes(state.get()) ? PlayerState.PLAY_REQUESTED : state.get()
      );
      this.startBufferingTimeout();
      onPlay(event);
    });
    this.eventManager.listen(this.videoElement, "pause", (event) => {
      this.onBufferingEnded();
      const { state } = this.state;
      state.set(PlayerState.PAUSED);
      const { onPause } = this.props;
      if (!this.isDestroying) {
        onPause(event);
      }
    });
    this.eventManager.listen(this.player, "error", ({ detail }) => this.onError(detail));
    this.eventManager.listen(this.player, "variantchanged", this.setAvailableAudioLanguages);
    this.eventManager.listen(this.player, "adaptation", this.setAvailableAudioLanguages);
    this.eventManager.listen(this.player, "textchanged", this.onSubtitleChanged);
    this.eventManager.listen(this.player, "texttrackvisibility", this.onSubtitleChanged);
    this.eventManager.listen(this.videoElement, "ended", (event) => {
      const { onEnded } = this.props;
      const { state } = this.state;
      state.set(PlayerState.LOADED);
      onEnded(event);
    });
    this.eventManager.listen(this.player, "buffering", (event) => {
      if (event.buffering) {
        this.onBuffering();
      } else {
        this.onBufferingEnded();
      }
    });
    this.eventManager.listen(this.videoElement, "seeking", this.startBufferingTimeout);
    this.eventManager.listen(this.videoElement, "seeked", this.onSeekedOrTimeShifted);
    this.eventManager.listen(this.player, "unloading", this.onSourceUnloaded);
    addFullscreenChangeEventListener(this.onFullscreenChange);
  }
  npawErrorParser(error) {
    const { npawAdapterOptions: { errorParser } } = this.props;
    return errorParser?.(normalizeError(error)) || defaultErrorParser(error);
  }
  npawErrorDetailsGetter(error) {
    const { npawAdapterOptions: { getErrorDetails } } = this.props;
    return getErrorDetails?.(normalizeError(error)) || normalizeError(error);
  }
  /**
   * Starts a timeout to call onBuffering.
   */
  startBufferingTimeout() {
    this.cancelBufferingTimeout();
    this.bufferingTimeout = setTimeout(this.onBuffering, BUFFERING_TIMEOUT_MS);
  }
  /**
   * Cancels an in progress timeout to call onBuffering
   */
  cancelBufferingTimeout() {
    if (this.bufferingTimeout)
      clearTimeout(this.bufferingTimeout);
  }
  resetState(callback) {
    const {
      state,
      time,
      duration,
      maxTimeShift,
      audioTracks,
      subtitleTracks
    } = this.state;
    state.set(PlayerState.UNLOADED);
    time.set(0);
    duration.set(0);
    maxTimeShift.set(0);
    audioTracks.set([]);
    subtitleTracks.set([]);
    callback();
  }
  /**
   * Unloads the current source in the player, clears progress interval, and cancels setCurrentTime.
   * Updates the state and resolves a promise when state is updated.
   * @returns {Promise<void>} A promise that resolves when state is updated
   */
  async unloadSource() {
    if (this.currentUnload)
      return this.currentUnload.promise;
    this.readyToPlay = false;
    this.currentUnload = {};
    this.currentUnload.promise = new Promise((resolve, reject) => {
      this.currentUnload.resolve = resolve;
      this.currentUnload.reject = reject;
    });
    this.clearProgressInterval();
    this.setCurrentTime.cancel();
    const { state } = this.state;
    state.set(PlayerState.UNLOADING);
    this.player.unload().then((result) => {
      this.resetState(() => {
        this.currentUnload.resolve(result);
        this.currentUnload = void 0;
      });
      return result;
    }).catch((error) => {
      this.currentUnload.reject(error);
      this.currentUnload = void 0;
    });
    return this.currentUnload.promise;
  }
  /**
   * Updates the DRM configuration of the player based on the key systems config
   * provided through props. It configures the servers, preferred key systems,
   * retry parameters, and advanced settings for each key system.
   * @returns {void}
   */
  updateDrmConfig() {
    const { keySystemsConfig } = this.props;
    const servers = Object.fromEntries(Object.keys(keySystemsConfig).filter((keySystem) => KEY_SYSTEM_NAMES[keySystem]).map((keySystem) => [
      KEY_SYSTEM_NAMES[keySystem],
      keySystemsConfig[keySystem]?.defaultLicenseUrl || TRY_TO_EXTRACT_FROM_INIT_DATA
    ]));
    this.player.configure("drm.servers", servers);
    if (keySystemsConfig.preferredKeySystems) {
      this.player.configure(
        "drm.preferredKeySystems",
        keySystemsConfig.preferredKeySystems.map((keySystem) => KEY_SYSTEM_NAMES[keySystem])
      );
    }
    if (keySystemsConfig.licenseRequest?.retries) {
      this.player.configure("drm.retryParameters.maxAttempts", keySystemsConfig.licenseRequest.retries + 1);
    }
    if (keySystemsConfig.licenseRequest?.timeout) {
      this.player.configure("drm.retryParameters.timeout", keySystemsConfig.licenseRequest.timeout * 1e3);
    }
    for (const keySystem of Object.keys(keySystemsConfig)) {
      const keySystemConfig = keySystemsConfig[keySystem];
      const keySystemName = KEY_SYSTEM_NAMES[keySystem];
      if (keySystemConfig?.persistentStateRequired) {
        this.player.configure("drm.advanced", { [keySystemName]: { persistentStateRequired: true } });
      }
    }
  }
  /**
   * Loads the current source in the player.
   * @returns {Promise<void>} A Promise that resolves when the source is loaded.
   */
  async loadSource() {
    const {
      source,
      startOffset,
      onSourceLoaded,
      isLive
    } = this.props;
    if (!source)
      return;
    try {
      await this.unloadSource();
    } catch {
    }
    const {
      state,
      audioTracks: audioTracksStore,
      subtitleTracks: subtitleTracksStore,
      maxTimeShift,
      npawAdapter
    } = this.state;
    state.set(PlayerState.LOADING);
    const manifestUrl = this.manifestForReload || getManifestUrlFromSource(source);
    this.updateDrmConfig();
    try {
      npawAdapter.setAssetUri(manifestUrl);
      let startTime;
      if (this.reloadTime) {
        startTime = isLive ? this.reloadTime - Date.now() / 1e3 : this.reloadTime;
      } else {
        startTime = startOffset;
      }
      await this.player.load(manifestUrl, isLive && startTime >= 0 ? void 0 : startTime);
      this.reloadTime = void 0;
      this.isErrorSources = false;
      const audioTracks = this.getAvailableAudio();
      const subtitleTracks = this.getAvailableSubtitles();
      state.set(PlayerState.LOADED);
      audioTracksStore.set(audioTracks);
      subtitleTracksStore.set(subtitleTracks);
      maxTimeShift.set(this.getMaxTimeShift());
      onSourceLoaded({ audioTracks, subtitleTracks });
    } catch (error) {
      console.error("[ShakaPlayer] Error loading source.", error);
      if (![
        ShakaError.Code.LOAD_INTERRUPTED,
        ShakaError.Code.OPERATION_ABORTED
      ].includes(error.code))
        this.onError(
          error,
          /* isFromLoad= */
          true
        );
      npawAdapter.onLoadFailed(error);
    }
  }
  /**
   * Starts playback in the player
   */
  async startPlayback() {
    if (!this.player)
      return;
    try {
      await this.play();
    } catch (error) {
      console.error("Shaka player > startPlayback error", error);
      const { onAutoplayFailed } = this.props;
      onAutoplayFailed();
    }
  }
  /**
   * @method togglePlayPause
   * @description Method that handle toggle event for play and pause.
   */
  togglePlayPause() {
    if (this.player.getMediaElement().paused) {
      this.play();
    } else {
      this.pause();
    }
  }
  async play() {
    return this.player.getMediaElement().play();
  }
  pause() {
    this.player.getMediaElement().pause();
  }
  /**
   * @method rewind
   * @description Method that handle the rewind of the 30 seconds.
   */
  rewind(secondsToRewind = DEFAULT_SECONDS_TO_REWIND) {
    const { seeking } = this.state;
    const { isLive } = this.props;
    seeking.set({
      isSeeking: true,
      time: isLive ? -secondsToRewind : this.player.getMediaElement().currentTime - secondsToRewind
    });
    this.player.getMediaElement().currentTime -= secondsToRewind;
  }
  /**
   * @method toggleMute
   * @description Method that handle volume mute or unmute.
   */
  toggleMute() {
    this.videoElement.muted = !this.videoElement.muted;
    this.onVolumeChanged();
  }
  /**
   * @function selectAudioTrack
   * @description set the new audio track as a variant track to the player
   * @param audioTrack - The selected audio track
   */
  selectAudioTrack(audioTrack) {
    const variantTracks = this.player.getVariantTracks();
    const currentTrack = variantTracks.find((track) => track.active);
    const sameVideoTracks = variantTracks.filter(({ videoId }) => videoId === currentTrack.videoId);
    const trackToSelect = sameVideoTracks.find(
      (track) => track.language === audioTrack.id
    );
    if (trackToSelect.audioId !== currentTrack.audioId) {
      this.player.selectVariantTrack(trackToSelect, true);
    }
  }
  /**
   * Enable a subtitle track in the player
   * @param subtitleTrack - The selected subtitle track
   */
  selectSubtitleTrack(subtitleTrack) {
    this.player.setTextTrackVisibility(true);
    this.player.selectTextTrack({
      ...subtitleTrack,
      id: subtitleTrack.originalShakaId,
      originalTextId: subtitleTrack.id
    });
  }
  /**
   * Disable subtitles tracks in the player
   */
  disableSubtitles() {
    this.player.setTextTrackVisibility(false);
  }
  /**
   * Goes to live edge
   */
  goToLive() {
    const { isLive } = this.props;
    if (isLive) {
      this.manualTimeShift = false;
      const { seeking } = this.state;
      seeking.set({ isSeeking: true, time: 0 });
      this.player.goToLive();
    }
  }
  render() {
    const {
      npawAdapter,
      npawAdsAdapter,
      state,
      buffering,
      seeking,
      time,
      duration,
      maxTimeShift,
      volume,
      audioTracks,
      subtitleTracks,
      viewMode,
      currentTime
    } = this.state;
    const {
      className,
      children
    } = this.props;
    return /* @__PURE__ */ jsx("div", { className: `dtv-common-c-shaka-player ${className}`, ref: this.setContainerElement, children: /* @__PURE__ */ jsx(
      Provider,
      {
        time,
        currentTime,
        state,
        buffering,
        seeking,
        subtitleTracks,
        audioTracks,
        duration,
        maxTimeShift,
        volume,
        viewMode,
        actions: this.playerActions,
        children: /* @__PURE__ */ jsxs(NpawAdapterProvider, { adapter: npawAdapter, adsAdapter: npawAdsAdapter, children: [
          /* @__PURE__ */ jsx("div", { ref: this.setAdContainerElement }),
          /* @__PURE__ */ jsx(
            "video",
            {
              className: "dtv-common-c-shaka-player__video",
              ref: this.setVideoElement
            }
          ),
          children
        ] })
      }
    ) });
  }
}
const keySystemConfigShape = PropTypes.shape({
  defaultLicenseUrl: PropTypes.string,
  getLicenseUrl: PropTypes.func,
  getLicenseHeaders: PropTypes.func,
  options: PropTypes.shape({})
});
ShakaPlayer.propTypes = {
  readyToBuild: PropTypes.bool,
  className: PropTypes.string,
  bufferConfig: PropTypes.shape({
    backward: PropTypes.number,
    forward: PropTypes.number
  }),
  config: PropTypes.shape({
    restrictions: PropTypes.shape({}),
    streaming: PropTypes.shape({})
  }),
  source: PropTypes.shape({
    dash: PropTypes.string,
    hls: PropTypes.string,
    smooth: PropTypes.string,
    isLowLatency: PropTypes.bool
  }),
  keySystemsConfig: PropTypes.shape({
    playready: keySystemConfigShape,
    widevine: keySystemConfigShape,
    fairplay: keySystemConfigShape,
    preferredKeySystems: PropTypes.arrayOf(PropTypes.string),
    licenseRequest: PropTypes.shape({
      timeout: PropTypes.number,
      retries: PropTypes.number
    })
  }),
  isLive: PropTypes.bool,
  shouldPlay: PropTypes.bool,
  adTag: PropTypes.shape({
    shouldBeRequested: PropTypes.func,
    getUri: PropTypes.func
  }),
  startOffset: PropTypes.number,
  userLanguages: PropTypes.shape({
    audio: PropTypes.string,
    subtitles: PropTypes.string
  }),
  lowLatency: PropTypes.shape({
    targetLatency: PropTypes.number,
    catchup: PropTypes.shape({
      playbackRate: PropTypes.number,
      playbackRateThreshold: PropTypes.number,
      seekThreshold: PropTypes.number
    }),
    fallback: PropTypes.shape({
      playbackRate: PropTypes.number,
      playbackRateThreshold: PropTypes.number,
      seekThreshold: PropTypes.number
    })
  }),
  recovery: PropTypes.shape({
    asWarning: PropTypes.shape({ errorCodes: PropTypes.instanceOf(Set) }),
    reload: PropTypes.shape({
      source: PropTypes.shape({
        dash: PropTypes.string,
        hls: PropTypes.string
      }),
      errorCodes: PropTypes.instanceOf(Set),
      shouldReload: PropTypes.func,
      skipSeconds: PropTypes.number,
      backoffOptions: PropTypes.shape({
        maxAttempts: PropTypes.number,
        baseDelay: PropTypes.number,
        backoffFactor: PropTypes.number,
        fuzzFactor: PropTypes.number,
        resetTime: PropTypes.number
      })
    })
  }),
  npawAdapterOptions: PropTypes.shape({
    errorParser: PropTypes.func,
    getErrorDetails: PropTypes.func
  }),
  avoidAutoPlay: PropTypes.bool,
  onFailedLicense: PropTypes.func,
  onDurationChange: PropTypes.func,
  onVolumeChanged: PropTypes.func,
  onFullScreenMode: PropTypes.func,
  onReadyToPlay: PropTypes.func,
  onEnded: PropTypes.func,
  onBuffering: PropTypes.func,
  onBufferingEnded: PropTypes.func,
  onSourceLoaded: PropTypes.func,
  onSourceUnloaded: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onPlaying: PropTypes.func,
  onNonPlayableSources: PropTypes.func,
  onHDCPError: PropTypes.func,
  onUnrecoverableError: PropTypes.func,
  onWarning: PropTypes.func,
  onProgress: PropTypes.func,
  onTracksEvent: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onAutoplayFailed: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  isCatchUpActive: PropTypes.bool,
  singleAudioTrackLanguage: PropTypes.string,
  crossSiteCredentials: PropTypes.shape({
    manifest: PropTypes.bool,
    segment: PropTypes.bool,
    license: PropTypes.bool
  }),
  getSegmentHeaders: PropTypes.func,
  getManifestHeaders: PropTypes.func,
  onRequest: PropTypes.func,
  onResponseReceived: PropTypes.func
};
ShakaPlayer.defaultProps = {
  readyToBuild: true,
  className: "",
  config: {},
  bufferConfig: void 0,
  source: void 0,
  keySystemsConfig: void 0,
  isLive: false,
  shouldPlay: false,
  adTag: void 0,
  startOffset: void 0,
  userLanguages: {},
  avoidAutoPlay: false,
  lowLatency: void 0,
  recovery: {},
  npawAdapterOptions: {},
  onDurationChange() {
  },
  onVolumeChanged() {
  },
  onFullScreenMode() {
  },
  onReadyToPlay() {
  },
  onEnded() {
  },
  onBuffering() {
  },
  onBufferingEnded() {
  },
  onSourceLoaded() {
  },
  onSourceUnloaded() {
  },
  onPlay() {
  },
  onPause() {
  },
  onPlaying() {
  },
  onNonPlayableSources() {
  },
  onHDCPError() {
  },
  onUnrecoverableError() {
  },
  onWarning() {
  },
  onProgress() {
  },
  onTracksEvent() {
  },
  onSeeking() {
  },
  onSeeked() {
  },
  onAutoplayFailed() {
  },
  onFailedLicense() {
  },
  children: void 0,
  isCatchUpActive: true,
  singleAudioTrackLanguage: void 0,
  crossSiteCredentials: {
    manifest: false,
    segment: false,
    license: false
  },
  getSegmentHeaders: () => {
  },
  getManifestHeaders: () => {
  },
  onRequest: void 0,
  onResponseReceived: () => {
  }
};

export { ShakaPlayer as default };
//# sourceMappingURL=ShakaPlayer.js.map
